var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
// @warp-css;
import { css, html, LitElement } from "lit";
import { state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { reset } from "../styles";
import { styles } from "./styles.js";
// all class objects have to be in this file when generating
const ccToastContainer = {
    wrapper: "fixed transform translate-z-0 bottom-16 left-0 right-0 mx-8 sm:mx-16 z-50 pointer-events-none",
    base: "grid auto-rows-auto justify-items-center justify-center mx-auto pointer-events-none",
    content: "w-full",
};
/**
 * You should probably not include this component in HTML markup.
 *
 * As long as the element definition is imported in your application you can use the toast JS API to create toasts.
 *
 * ```js
 * import from '@warp-ds/elements/components/toast';
 * import { toast, removeToast, updateToast } from '@warp-ds/elements/toast';
 * ```
 *
 * [See Storybook for usage examples](https://warp-ds.github.io/elements/?path=/docs/overlays-toast--docs)
 *
 * @internal
 */
export class WarpToastContainer extends LitElement {
    constructor() {
        super(...arguments);
        this._toasts = new Map();
    }
    static { this.styles = [
        reset,
        styles,
        css `
			:host {
				display: block;
			}
		`,
    ]; }
    connectedCallback() {
        super.connectedCallback();
        // regularly check if any toasts have expired
        this._interval = setInterval(() => {
            // sort toasts into keep and remove
            const keep = [];
            const remove = [];
            for (const toast of this._toasts) {
                if (Date.now() <= toast[1].duration)
                    keep.push(toast);
                else
                    remove.push(toast);
            }
            // collapse toasts that will be removed
            const collapseTasks = [];
            for (const [id] of remove) {
                const el = this.renderRoot.querySelector(`#${id}`);
                if (el)
                    collapseTasks.push(el.collapse());
            }
            // once all toasts that should be removed have been collapsed, remove them from the map
            Promise.all(collapseTasks).then(() => {
                if (keep.length !== this._toasts.size) {
                    this._toasts = new Map(keep);
                }
            });
        }, 500);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._interval) {
            clearTimeout(this._interval);
        }
    }
    static init() {
        let el = document.querySelector("w-toast-container");
        if (!el) {
            el = document.createElement("w-toast-container");
            document.body.appendChild(el);
        }
        return el;
    }
    /** @internal */
    get _toastsArray() {
        return Array.from(this._toasts, ([, toast]) => toast);
    }
    get(id) {
        if (!id) {
            throw new Error('undefined "id" given when attempting to retrieve toast');
        }
        if (typeof id !== "string" && !Number.isInteger(id)) {
            throw new Error('"id" must be number or string when attempting to retrieve toast');
        }
        return this._toasts.get(id);
    }
    set(toast) {
        if (!toast.id) {
            throw new Error('invalid or undefined "id" on toast object');
        }
        const result = this._toasts.set(toast.id, {
            id: toast.id,
            type: toast.type ?? "success",
            text: toast.text ?? "",
            canclose: toast.canclose ?? false,
            duration: Date.now() + (toast.duration || 5000),
        });
        this._toasts = new Map([...this._toasts]);
        return result;
    }
    async del(id) {
        if (!id) {
            throw new Error('undefined "id" given when attempting to retrieve toast');
        }
        if (typeof id !== "string" && !Number.isInteger(id)) {
            throw new Error('"id" must be number or string when attempting to retrieve toast');
        }
        const el = this.renderRoot.querySelector(`#${id}`);
        if (!this._toasts.has(id) || !el) {
            return false;
        }
        await el.collapse();
        const result = this._toasts.delete(id);
        this._toasts = new Map([...this._toasts]);
        return result;
    }
    render() {
        return html `
			<aside class="${ccToastContainer.wrapper}">
				<div class="${ccToastContainer.base}" id="w-toast-container-list">
					${repeat(this._toastsArray, (toast) => toast.id, (toast) => html ` <w-toast
								class="${ccToastContainer.content}"
								id="${toast.id}"
								type="${toast.type}"
								text="${toast.text}"
								?canclose=${toast.canclose}
								@close=${() => this.del(toast.id)}
							>
							</w-toast>`)}
				</div>
			</aside>
		`;
    }
}
__decorate([
    state()
], WarpToastContainer.prototype, "_toasts", void 0);
if (!customElements.get("w-toast-container")) {
    customElements.define("w-toast-container", WarpToastContainer);
}
//# sourceMappingURL=toast-container.js.map